import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StoreEndpointsConstant } from '@constants/store-endpoints.constant';
import {
  ActivityDataDeleteOrderResult,
  ActivityDataDeleteResult,
  ActivityDataToDelete,
  SessionActivityOrder
} from '@models/activity.model';
import { FieldsData, FileDelete, FileUpload } from '@models/checkout.model';
import { NotificationResponse } from '@models/notifications.model';
import { ProgressStepper } from '@models/progress-stepper.model';
import { CartInfo, CostInfo } from '@models/session-details';
import {
  CustomElementValue,
  FinalizeShoppingCartData,
  PriceData,
  SummaryInfo
} from '@models/shopping-cart.model';
import {
  OrderDetailsSummary,
  ShoppingCartData,
  ShoppingCartItem,
  StoreOrderInfo
} from '@models/shopping-cart.model';
import {
  AccessoriesData,
  CART_ITEM_TYPE_ACCESSORY,
  ClientSettings,
  ColorsListResponse,
  DeviceData,
  DeviceIdentifier,
  DeviceItemCarrier,
  DeviceCarriersListResponse,
  DeviceItemData,
  DeviceItemDevice,
  DeviceItemDeviceBP,
  FilterToken,
  ManageAddOnFeatures,
  ManageBusinessPlans,
  NameOverrideListResponse,
  OrderApproversLookupData,
  OrderApproversResponse,
  OrderTypeSettings,
  PreFlight,
  ReviewFields,
  ShoppingDataInitialLoad,
  StorageListResponse,
  ValidateFields
} from '@models/shopping.model';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { CustomFormService } from '@services/custom-form/custom-form.service';
import { FilterStateService } from '@services/filter-state/filter-state.service';
import { requestTrackingContext } from '@services/requests-tracker/requests-tracker.service';
import { SessionService } from '@services/session/session.service';
import { UrlUtils } from '@utils/url.utils';
import * as lodash from 'lodash';
import { Observable, catchError, forkJoin, map, mergeMap, of, tap } from 'rxjs';
import { dataLoaderContext } from 'tangoe-design-system';

@Injectable({
  providedIn: 'root'
})
export class ShoppingService {
  private shop_layout_title: SummaryInfo;
  public preselected_item: boolean = false;
  private client_settings_data?: ClientSettings;

  constructor(
    private activity_state_svc: ActivityStateService,
    private custom_form_svc: CustomFormService,
    private filter_state_svc: FilterStateService,
    private http: HttpClient,
    private session_svc: SessionService,
    private url_utl: UrlUtils
  ) {}

  set shopLayoutTitle(value: SummaryInfo) {
    this.shop_layout_title = value;
  }

  get shopLayoutTitle(): SummaryInfo {
    return this.shop_layout_title;
  }

  sortColorByName(item_list: DeviceItemDevice[]) {
    return item_list.sort((a: DeviceItemDevice, b: DeviceItemDevice) =>
      a.color.toLowerCase() > b.color.toLowerCase()
        ? 1
        : b.color.toLowerCase() > a.color.toLowerCase()
          ? -1
          : 0
    );
  }

  uniqColorByName(item_list: DeviceItemDevice[] | DeviceItemDeviceBP[]): any {
    return lodash.uniqBy(item_list, 'color');
  }

  getDevices(initial_load: ShoppingDataInitialLoad, key?: string): Observable<DeviceData> {
    const payload = {
      ...initial_load,
      orderTypeId: this.activity_state_svc.parameter('type')?.order_type_id,
      countryId: this.activity_state_svc.parameter('country')?.id,
      filter_tokens: this.filter_state_svc.filtersParam.filters,
      deviceType: this.activity_state_svc.parameter('device_type'),
      deviceCatalogId: this.activity_state_svc.parameter('device_catalog_id'),
      lineId: this.activity_state_svc.parameter('lineId'),
      keep_plan: this.filter_state_svc.filtersParam.keep_plan,
      carrierId: this.activity_state_svc.parameter('carrier_id'),
      excludeCarrierId: this.activity_state_svc.parameter('excluded_carrier_id')
    };
    return this.http.post<DeviceData>(StoreEndpointsConstant.DEVICES, payload, {
      context: dataLoaderContext(key ?? 'devices-list')
    });
  }

  getDevicesForTheirFilters(
    categories: ShoppingDataInitialLoad['categories'],
    filter_tokens: FilterToken[] = [],
    include_admin_options?: boolean
  ): Observable<DeviceData> {
    return this.http.post<DeviceData>(
      StoreEndpointsConstant.DEVICES,
      {
        ...this.filter_state_svc.defaultFilters(),
        orderTypeId: this.activity_state_svc.parameter('type')?.order_type_id,
        countryId: this.activity_state_svc.parameter('country')?.id,
        categories,
        filter_tokens,
        include_admin_options: include_admin_options ?? undefined,
        filters: [...(filter_tokens ?? [])]
      },
      {
        context: dataLoaderContext('devices-creation-form')
      }
    );
  }

  getPlans(
    carrierId: number,
    param: { [key: string]: number | string | boolean } = {},
    key?: string
  ): Observable<ManageBusinessPlans> {
    return this.http.get<ManageBusinessPlans>(StoreEndpointsConstant.PLAN_DETAILS, {
      params: {
        ...param,
        orderTypeId: this.activity_state_svc.parameter('type')?.order_type_id!,
        carrierId
      },
      context: dataLoaderContext(key ?? 'device-plans')
    });
  }

  getFeatures(
    carrierId: number,
    param: { [key: string]: number | string | boolean } = {},
    key?: string
  ): Observable<ManageAddOnFeatures> {
    return this.http.get<ManageAddOnFeatures>(StoreEndpointsConstant.FEATURE_DETAILS, {
      params: {
        ...param,
        orderTypeId: this.activity_state_svc.parameter('type')?.order_type_id!,
        carrierId
      },
      context: dataLoaderContext(key ?? 'device-plan-features')
    });
  }

  getAccessories(initial_load: ShoppingDataInitialLoad, key?: string): Observable<AccessoriesData> {
    const payload = {
      ...initial_load,
      filter_tokens: this.filter_state_svc.filtersParam.filters
    };

    return this.http.post<AccessoriesData>(StoreEndpointsConstant.ACCESSORIES, payload, {
      context: dataLoaderContext(key ?? 'accessories-list')
    });
  }

  getShoppingItems(key?: string): Observable<ShoppingCartData> {
    return this.http
      .get<ShoppingCartData>(StoreEndpointsConstant.SHOPPING_CART, {
        context: dataLoaderContext(key ?? 'shopping-cart-items')
      })
      .pipe(tap((res: ShoppingCartData) => this.activity_state_svc.cartUpdated(res)));
  }

  sendShoppingItems(
    shopping_data: ShoppingCartData,
    reset_cart: boolean = false,
    key?: string
  ): Observable<ShoppingCartData> {
    const payload: ShoppingCartData = {
      ...shopping_data,
      country_id: shopping_data.country_id ?? this.activity_state_svc.parameter('country')?.id,
      authorized_to_place_order: undefined
    };

    return reset_cart
      ? this.http
          .post<ShoppingCartData>(StoreEndpointsConstant.SHOPPING_CART, payload, {
            context: key ? dataLoaderContext(key) : undefined
          })
          .pipe(tap((res: ShoppingCartData) => this.activity_state_svc.cartUpdated(res)))
      : this.http
          .put<ShoppingCartData>(StoreEndpointsConstant.SHOPPING_CART, payload, {
            context: key ? dataLoaderContext(key) : undefined
          })
          .pipe(tap((res: ShoppingCartData) => this.activity_state_svc.cartUpdated(res)));
  }

  updateShoppingItems(
    shopping_data: ShoppingCartData,
    increment: boolean
  ): Observable<ShoppingCartData> {
    const params: { [key: string]: string | number | boolean } = {
      id: shopping_data.shopping_cart_items[0].id,
      type: CART_ITEM_TYPE_ACCESSORY,
      action: increment ? 'increment' : 'decrement'
    };

    if (shopping_data.accessories_include_admin_options != undefined)
      params['accessories_include_admin_options'] = shopping_data.accessories_include_admin_options;

    return this.http
      .patch<ShoppingCartData>(StoreEndpointsConstant.SHOPPING_CART, shopping_data, {
        params: params
      })
      .pipe(tap((res: ShoppingCartData) => this.activity_state_svc.cartUpdated(res)));
  }

  deleteCartItem(cart_item: ShoppingCartItem): Observable<ShoppingCartData> {
    return this.http
      .delete<ShoppingCartData>(StoreEndpointsConstant.SHOPPING_CART, {
        params: {
          id: cart_item.id,
          type: cart_item.type
        }
      })
      .pipe(tap((res: ShoppingCartData) => this.activity_state_svc.cartUpdated(res)));
  }

  clearShoppingCartItems(key?: string): Observable<ShoppingCartData> {
    return this.http
      .delete<ShoppingCartData>(StoreEndpointsConstant.SHOPPING_CART_CLEAR, {
        context: key ? dataLoaderContext(key) : undefined
      })
      .pipe(tap((res: ShoppingCartData) => this.activity_state_svc.cartUpdated(res)));
  }

  clearSpecifiedCartItems(
    ids: number[],
    remove_carrier: boolean = true,
    key?: string
  ): Observable<ShoppingCartData> {
    let ids_param: string = '';
    ids.forEach((id: number, index: number = 0) => {
      ids_param = ids_param + id.toString();
      if (index < ids.length - 1) ids_param = ids_param + ',';
      index++;
    });

    return this.http
      .delete<ShoppingCartData>(StoreEndpointsConstant.DELETE_CART_ITEMS, {
        params: {
          ids: ids_param,
          remove_carrier: remove_carrier
        },
        context: key ? dataLoaderContext(key) : undefined
      })
      .pipe(tap((res: ShoppingCartData) => this.activity_state_svc.cartUpdated(res)));
  }

  getShoppingPrice(shopping_cart: ShoppingCartData): Observable<PriceData> {
    return this.http.post<PriceData>(StoreEndpointsConstant.SHOPPING_CART_PRICE, shopping_cart, {
      context: dataLoaderContext('cart-prices')
    });
  }

  getShoppingCartSummary(): Observable<ShoppingCartData> {
    return this.http.get<ShoppingCartData>(StoreEndpointsConstant.SHOPPING_CART_SUMMARY);
  }

  getOrderApprovers(id: number, skip_approval?: boolean): Observable<OrderApproversResponse> {
    let params = new HttpParams();

    if (skip_approval !== undefined) params = params.set('skip_approval', String(skip_approval));

    return this.http.get<OrderApproversResponse>(
      this.url_utl.parseURL(StoreEndpointsConstant.APPROVERS, { id }),
      { params }
    );
  }

  searchOrderApprovers(
    term: string,
    type: string,
    approver_id: number | null = null
  ): Observable<OrderApproversLookupData> {
    let order_params: {
      searchQuery: string;
      type: string;
      approverId?: number;
    } = approver_id
      ? {
          searchQuery: term,
          type: type,
          approverId: approver_id
        }
      : {
          searchQuery: term,
          type: type
        };

    return this.http.get<OrderApproversLookupData>(StoreEndpointsConstant.APPROVERS_SEARCH, {
      params: order_params
    });
  }

  getFields(
    shopping_cart: FinalizeShoppingCartData,
    data_loader_key?: string
  ): Observable<FieldsData> {
    return this.http.post<FieldsData>(StoreEndpointsConstant.FIELDS, shopping_cart, {
      context: dataLoaderContext(data_loader_key ?? 'order-fields')
    });
  }

  uploadFile(id: number, file_list: FileList): Observable<FileUpload> {
    const formData: FormData = new FormData();
    formData.append('file', file_list[0]);

    return this.http.put<FileUpload>(
      this.url_utl.parseURL(StoreEndpointsConstant.UPLOAD_FILE, { id: id }),
      formData
    );
  }

  deleteFile(id: number): Observable<FileDelete> {
    return this.http.delete<FileDelete>(
      this.url_utl.parseURL(StoreEndpointsConstant.UPLOAD_FILE, { id: id })
    );
  }

  getReviewFields(
    id: number,
    payload: StoreOrderInfo,
    data_loader_key?: string
  ): Observable<ReviewFields> {
    return this.http.post<ReviewFields>(
      this.url_utl.parseURL(StoreEndpointsConstant.FIELDS_REVIEW, { id: id }),
      payload,
      {
        context: dataLoaderContext(data_loader_key ?? 'order-fields')
      }
    );
  }

  validateFields(body: FinalizeShoppingCartData): Observable<ValidateFields> {
    const payload: FormData = this.custom_form_svc.buildFormData({
      ...body,
      authorized_to_place_order: undefined
    });

    return this.http.post<ValidateFields>(StoreEndpointsConstant.FIELDS_VALIDATE, payload);
  }

  validateReviewFields(id: number, payload: StoreOrderInfo): Observable<ValidateFields> {
    return this.http.post<ValidateFields>(
      this.url_utl.parseURL(StoreEndpointsConstant.FIELDS_REVIEW_VALIDATE, { id: id }),
      payload
    );
  }

  placeOrder(
    body: FinalizeShoppingCartData,
    id: number | undefined
  ): Observable<OrderDetailsSummary> {
    const payload: FormData = this.custom_form_svc.buildFormData({
      ...body,
      authorized_to_place_order: undefined
    });
    const url: string = id
      ? this.url_utl.parseURL(StoreEndpointsConstant.ORDERS, { id: id })
      : StoreEndpointsConstant.UPDATE_ORDERS;
    return id
      ? this.http
          .put<OrderDetailsSummary>(url, payload, {
            context: requestTrackingContext(0)
          })
          .pipe(
            tap((res: OrderDetailsSummary) => {
              if (res?.id) this.activity_state_svc.registerActivityOrder(res.id, body.orderTypeId);
            })
          )
      : this.http
          .post<OrderDetailsSummary>(url, payload, {
            context: requestTrackingContext(0)
          })
          .pipe(
            tap((res: OrderDetailsSummary) => {
              if (res?.id) this.activity_state_svc.registerActivityOrder(res.id, body.orderTypeId);
            })
          );
  }

  updatePlacedOrder(id: number, order_param: StoreOrderInfo): Observable<OrderDetailsSummary> {
    return this.http.put<OrderDetailsSummary>(
      this.url_utl.parseURL(StoreEndpointsConstant.SUBMIT_ORDERS, { id: id }),
      order_param,
      {
        context: requestTrackingContext({ count: 0, ignore_400_range: [422] })
      }
    );
  }

  deleteOrder(id: number, key?: string): Observable<boolean> {
    return this.http
      .delete<OrderDetailsSummary>(
        this.url_utl.parseURL(StoreEndpointsConstant.ORDERS, { id: id }),
        {
          context: requestTrackingContext({
            count: 0,
            context: key ? dataLoaderContext(key) : undefined
          })
        }
      )
      .pipe(
        catchError(() => of(false)),
        map((res: OrderDetailsSummary | boolean) => Boolean(res))
      );
  }

  getOrderDetails(id: number, key?: string): Observable<OrderDetailsSummary> {
    return this.http.get<OrderDetailsSummary>(
      this.url_utl.parseURL(StoreEndpointsConstant.ORDERS, { id: id }),
      {
        context: dataLoaderContext(key ?? 'order-details')
      }
    );
  }

  getWhatNextNotification(id: number): Observable<NotificationResponse> {
    return this.http.get<NotificationResponse>(StoreEndpointsConstant.WHAT_NEXT_NOTIFICATIONS, {
      params: { storeOrderId: id },
      context: dataLoaderContext('global')
    });
  }

  getDenialNotification(id: number): Observable<NotificationResponse> {
    return this.http.get<NotificationResponse>(StoreEndpointsConstant.DENIAL_NOTIFICATIONS, {
      params: { storeOrderId: id },
      context: dataLoaderContext('global')
    });
  }

  getPlanSettings(id: number): Observable<OrderTypeSettings> {
    return this.http.get<OrderTypeSettings>(StoreEndpointsConstant.ORDER_TYPE_SETTINGS, {
      params: { orderTypeId: id }
    });
  }

  loadDefaultCartItems(): Observable<ShoppingCartData | undefined> {
    return this.getShoppingItems('global').pipe(
      catchError(() => of(undefined)),
      tap((res: ShoppingCartData | undefined) => {
        if (res) {
          this.activity_state_svc.cartUpdated(res);
        }
        return res;
      })
    );
  }

  deleteUserActivity(data_to_delete: ActivityDataToDelete): Observable<ActivityDataDeleteResult> {
    const cart_observable: Observable<boolean> = data_to_delete.cart
      ? this.clearShoppingCartItems('delete-user-activity').pipe(
          catchError(() => of(false)),
          tap(() => {
            CostInfo.one_time_cost = '- -';
            CostInfo.recurring_cost = '- -';
            CartInfo.cart_data = undefined;
          }),
          map((res: ShoppingCartData | boolean) => Boolean(res))
        )
      : of(false);

    const order_observables: Observable<ActivityDataDeleteOrderResult>[] =
      data_to_delete.orders.map((order: SessionActivityOrder) =>
        this.getOrderDetails(order.id, 'delete-user-activity').pipe(
          catchError(() => of(undefined)),
          mergeMap((res: OrderDetailsSummary | undefined) => {
            if (!res) return of(false);
            if (res.source_order.source_system_status.toLowerCase() !== 'unconfirmed')
              return of(true);
            return this.deleteOrder(order.id, 'delete-user-activity');
          }),
          map((res: boolean) => ({ id: order.id, deleted: res }))
        )
      );

    return forkJoin<[boolean, ...ActivityDataDeleteOrderResult[]]>([
      cart_observable,
      ...order_observables
    ]).pipe(
      map(([cart, ...orders]: [boolean, ...ActivityDataDeleteOrderResult[]]) => ({
        cart,
        orders
      }))
    );
  }

  downloadFile(
    order_id: number,
    value: Partial<CustomElementValue>
  ): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>(
      this.url_utl.parseURL(StoreEndpointsConstant.CUSTOM_FILE, {
        order_id: order_id,
        id: value.id
      }),
      {
        params: { output_file_name: value.file_name! },
        observe: 'response',
        responseType: 'blob' as 'json',
        context: dataLoaderContext('global')
      }
    );
  }

  deviceIdentifierValidate(params: DeviceIdentifier): Observable<ValidateFields> {
    return this.http.get<ValidateFields>(StoreEndpointsConstant.DEVICE_IDENTIFIER_VALIDATE, {
      params: {
        ...params
      },
      context: dataLoaderContext('devices-list')
    });
  }

  clientSettings(key?: string): Observable<ClientSettings> {
    return this.http
      .get<ClientSettings>(
        this.url_utl.parseURL(StoreEndpointsConstant.CLIENT_SETTINGS, {
          id: this.session_svc.getTenantInfo().id!
        }),
        {
          context: dataLoaderContext(key ?? 'global')
        }
      )
      .pipe(
        tap((settings: ClientSettings) => {
          this.client_settings_data = settings;
        })
      );
  }

  getClientSettings(): ClientSettings | undefined {
    return this.client_settings_data;
  }

  isPendoEnabled(): boolean {
    return this.client_settings_data?.enable_pendo === true;
  }

  carrierValidation(payload: PreFlight): Observable<ValidateFields> {
    return this.http.post<ValidateFields>(StoreEndpointsConstant.CARRIER_VALIDATION, payload);
  }

  getStorageList(
    payload: DeviceData,
    carrier_id: number,
    color: string
  ): Observable<StorageListResponse> {
    return this.http.post<StorageListResponse>(
      this.url_utl.parseURL(StoreEndpointsConstant.STORAGE_LIST, {
        carrier_id: carrier_id,
        color_name: color
      }),
      payload
    );
  }

  getNameOverridesList(
    payload: DeviceData,
    carrier_id: number,
    color: string,
    size: string
  ): Observable<NameOverrideListResponse> {
    return this.http.post<any>(
      this.url_utl.parseURL(StoreEndpointsConstant.NAME_OVERRIDES_LIST, {
        carrier_id: carrier_id,
        color_name: color,
        size: size
      }),
      payload
    );
  }

  getCarriersList(payload: DeviceData): Observable<DeviceCarriersListResponse> {
    return this.http.post<DeviceCarriersListResponse>(StoreEndpointsConstant.CARRIER_LIST, payload);
  }

  getColorsList(payload: DeviceData, id: number): Observable<ColorsListResponse> {
    return this.http.post<ColorsListResponse>(
      this.url_utl.parseURL(StoreEndpointsConstant.COLORS_LIST, { carrier_id: id }),

      payload
    );
  }

  getProgessBarForTransferLiability(): Observable<ProgressStepper> {
    return this.http.get<ProgressStepper>(StoreEndpointsConstant.GET_LIABILITY_PROGRESS_STEPPER);
  }

  // return this.http.post<ValidateFields>(
  //     this.url_utl.parseURL(StoreEndpointsConstant.FIELDS_REVIEW_VALIDATE, { id: id }),
  //     payload
  //   );
}
